if ( typeof ShippingNotice !== 'function' ) {

  class ShippingNotice extends HTMLElement {

    constructor() {
      super();
      this.init();
    }

    init() {

      const currencyRate = Shopify.currency.rate ? Number(Shopify.currency.rate) : 1;
      const freeShippingThreshold = Math.round(Number(this.getAttribute('data-free-shipping')) * currencyRate);
      const cartTotal = Number(this.getAttribute('data-cart-total'));
      const freeShippingRemaining = cartTotal - freeShippingThreshold;

      const tierThresholdsRaw = (this.getAttribute('data-tier-thresholds') || '').split(',').filter(Boolean).map(Number);
      const tiers = tierThresholdsRaw.map((threshold) => Math.round(threshold * currencyRate));

      const textEl = this.querySelector('[data-js-free-shipping-text]');
      const progressBar = this.querySelector('[data-js-free-shipping-slider]');
      const sliderTrack = this.querySelector('[data-js-tier-slider]');
      const shipMarker = sliderTrack ? sliderTrack.querySelector('[data-shipping-marker]') : null;
      const tierMarkerEls = sliderTrack ? Array.from(sliderTrack.querySelectorAll('[data-tier-marker]')) : [];

      let currentTierIndex = -1;
      if (freeShippingRemaining >= 0) {
        tiers.forEach((threshold, i) => {
          if (cartTotal >= threshold) {
            currentTierIndex = i;
          }
        });
      }

      if (textEl) {
        if (freeShippingRemaining < 0) {
          textEl.innerHTML = window.KROWN.settings.locales.tier_pending.replace('{{ remaining_amount }}', this._formatMoney(Math.abs(freeShippingRemaining), KROWN.settings.shop_money_format));
        } else if (currentTierIndex === -1 && tiers.length) {
          const remaining = tiers[0] - cartTotal;
          textEl.innerHTML = window.KROWN.settings.locales.tier_shipping_unlocked.replace('{{ remaining_amount }}', this._formatMoney(remaining, KROWN.settings.shop_money_format));
        } else if (currentTierIndex === -1) {
          textEl.innerHTML = window.KROWN.settings.locales.shipping_notice_eligible_for_free;
        } else if (currentTierIndex === 0 && tiers.length > 1) {
          const remaining = tiers[1] - cartTotal;
          textEl.innerHTML = window.KROWN.settings.locales.tier_wholesale_unlocked.replace('{{ remaining_amount }}', this._formatMoney(remaining, KROWN.settings.shop_money_format));
        } else if (currentTierIndex === 1 && tiers.length > 2) {
          const remaining = tiers[2] - cartTotal;
          textEl.innerHTML = window.KROWN.settings.locales.tier_bulk_unlocked.replace('{{ remaining_amount }}', this._formatMoney(remaining, KROWN.settings.shop_money_format));
        } else if (currentTierIndex === tiers.length - 1) {
          textEl.innerHTML = window.KROWN.settings.locales.tier_max_reached;
        }
      }

      /*
       * Two-phase bar, no gradient:
       *  Phase 0 (before free shipping is hit): bar fills 0-100% across
       *  £0 -> free shipping threshold, solid red.
       *  Phase 1 (free shipping hit): the bar resets to the beginning and
       *  now represents progress across the tier thresholds only, spaced
       *  evenly (not proportional to their pound values). The whole bar
       *  snaps to the colour of the stage the cart is currently in.
       */
      const stageColours = ['#d4123b', '#ff6633', '#f2c94c', '#2f7d3c', '#ffffff'];
      let stage = 0;
      if (cartTotal >= freeShippingThreshold) {
        stage = 1;
        tiers.forEach((threshold, i) => {
          if (cartTotal >= threshold) {
            stage = i + 2;
          }
        });
      }
      stage = Math.min(stage, stageColours.length - 1);

      let fillPercent;
      if (cartTotal < freeShippingThreshold) {
        fillPercent = freeShippingThreshold > 0 ? (Math.max(cartTotal, 0) / freeShippingThreshold) * 100 : 0;
      } else {
        const segmentPct = tiers.length ? 100 / tiers.length : 0;
        let prevThreshold = freeShippingThreshold;
        let prevPct = 0;
        fillPercent = 100;
        for (let i = 0; i < tiers.length; i++) {
          const threshold = tiers[i];
          const pct = segmentPct * (i + 1);
          if (cartTotal <= threshold) {
            const span = threshold - prevThreshold;
            const ratio = span > 0 ? (cartTotal - prevThreshold) / span : 1;
            fillPercent = prevPct + ratio * (pct - prevPct);
            break;
          }
          prevThreshold = threshold;
          prevPct = pct;
        }
      }
      fillPercent = Math.max(0, Math.min(100, fillPercent));

      if (progressBar) {
        progressBar.style.width = `${fillPercent}%`;
        progressBar.style.backgroundImage = 'none';
        progressBar.style.backgroundColor = stageColours[stage];
      }

      /*
       * Dots: a dot that's been reached disappears. Of the ones still
       * ahead, the very next one to be reached is washi (the immediate
       * goal); every dot beyond that is baize.
       */
      if (sliderTrack) {

        if (shipMarker) {
          const hit = cartTotal >= freeShippingThreshold;
          if (hit) {
            shipMarker.style.display = 'none';
          } else {
            shipMarker.style.display = '';
            shipMarker.style.left = '100%';
            shipMarker.style.backgroundColor = '#fcfae8';
            shipMarker.style.borderColor = '#fcfae8';
          }
        }

        const segmentPct = tiers.length ? 100 / tiers.length : 0;
        let nextAssigned = false;

        tierMarkerEls.forEach((marker, i) => {
          const threshold = tiers[i];
          if (threshold === undefined) {
            marker.style.display = 'none';
            return;
          }

          const hit = cartTotal >= threshold;

          if (cartTotal < freeShippingThreshold) {
            marker.style.display = 'none';
            return;
          }

          if (hit) {
            marker.style.display = 'none';
            return;
          }

          marker.style.display = '';
          marker.style.left = `${segmentPct * (i + 1)}%`;

          if (!nextAssigned) {
            marker.style.backgroundColor = '#fcfae8';
            marker.style.borderColor = '#fcfae8';
            nextAssigned = true;
          } else {
            marker.style.backgroundColor = '#36453d';
            marker.style.borderColor = '#36453d';
          }
        });

      }

    }

    _formatMoney(cents, format) {

			if (typeof cents === 'string') {
				cents = cents.replace('.', '');
			}

			let value = '';
			const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
			const formatString = format || moneyFormat;

			function formatWithDelimiters(number, precision, thousands, decimal) {
				thousands = thousands || ',';
				decimal = decimal || '.';

				if (isNaN(number) || number === null) {
					return 0;
				}

				number = (number / 100.0).toFixed(precision);

				const parts = number.split('.');
				const dollarsAmount = parts[0].replace(
					/(\d)(?=(\d\d\d)+(?!\d))/g,
					'$1' + thousands
				);
				const centsAmount = parts[1] ? decimal + parts[1] : '';

				return dollarsAmount + centsAmount + KROWN.settings.iso_code;
			}

			switch (formatString.match(placeholderRegex)[1]) {
				case 'amount':
					value = formatWithDelimiters(cents, 2);
					break;
				case 'amount_no_decimals':
					value = formatWithDelimiters(cents, 0);
					break;
				case 'amount_with_comma_separator':
					value = formatWithDelimiters(cents, 2, '.', ',');
					break;
				case 'amount_no_decimals_with_comma_separator':
					value = formatWithDelimiters(cents, 0, '.', ',');
					break;
				case 'amount_no_decimals_with_space_separator':
					value = formatWithDelimiters(cents, 0, ' ');
					break;
				case 'amount_with_apostrophe_separator':
					value = formatWithDelimiters(cents, 2, "'");
					break;
			}

			return formatString.replace(placeholderRegex, value);

		}

  }

  if ( typeof customElements.get('shipping-notice') == 'undefined' ) {
    customElements.define('shipping-notice', ShippingNotice);
  }

}
